import React, { useState, useRef, useEffect } from 'react';
import { Menu, ChevronDown } from 'lucide-react';
import { DriverStatus } from '@/types';

// Status types for the dropdown that match backend enum values
// Only allowing new granular statuses + DISPATCHED (excluding old ones like ACTIVE, CLEAR, ON_LEAVE)
type StatusOption =
  | 'DISPATCHED'
  | 'WAITING'
  | 'EN_ROUTE'
  | 'ON_SCENE'
  | 'TOWING'
  | 'RETURNING'
  | 'OFFLINE';

interface HeaderProps {
  driverName: string;
  status: DriverStatus;
  onMenuClick?: () => void;
  onStatusClick?: () => void;
  onStatusChange?: (newStatus: StatusOption) => void;
  isEmbeddedMode?: boolean;
  canEditStatus?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  driverName,
  status,
  onMenuClick,
  onStatusClick,
  onStatusChange,
  isEmbeddedMode = false,
  canEditStatus = true,
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<StatusOption>('OFFLINE');
  const statusButtonRef = useRef<HTMLButtonElement>(null);

  // Set current status based on the driver's status from props
  useEffect(() => {
    if (status && status !== 'unknown') {
      setCurrentStatus(status as StatusOption);
    }
  }, [status]);

  const getStatusColor = (statusOption: StatusOption) => {
    switch (statusOption) {
      case 'WAITING':
        return 'bg-gray-300';
      case 'DISPATCHED':
        return 'bg-green-400';
      case 'EN_ROUTE':
        return 'bg-blue-400';
      case 'ON_SCENE':
        return 'bg-orange-400';
      case 'TOWING':
        return 'bg-cyan-400';
      case 'RETURNING':
        return 'bg-purple-400';
      case 'OFFLINE':
        return 'bg-gray-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (statusOption: StatusOption) => {
    switch (statusOption) {
      case 'DISPATCHED':
        return 'Dispatched';
      case 'WAITING':
        return 'Waiting';
      case 'EN_ROUTE':
        return 'En Route';
      case 'ON_SCENE':
        return 'On Scene';
      case 'TOWING':
        return 'Towing';
      case 'RETURNING':
        return 'Returning';
      case 'OFFLINE':
        return 'Offline';
      default:
        return 'Set Status';
    }
  };

  const handleStatusSelect = (newStatus: StatusOption) => {
    setCurrentStatus(newStatus);
    setShowStatusDropdown(false);

    // Call the original onStatusClick if provided
    if (onStatusClick) {
      onStatusClick();
    }

    // Call the new onStatusChange if provided
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
  };

  const toggleStatusDropdown = () => {
    setShowStatusDropdown(!showStatusDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const statusButton = statusButtonRef.current;
      const statusDropdown = document.querySelector('[data-status-dropdown]');

      if (
        statusButton &&
        !statusButton.contains(target) &&
        statusDropdown &&
        !statusDropdown.contains(target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const statusOptions: StatusOption[] = [
    'WAITING',
    'DISPATCHED',
    'EN_ROUTE',
    'ON_SCENE',
    'TOWING',
    'RETURNING',
    'OFFLINE',
  ];

  return (
    <div className="bg-primary text-primary-foreground px-4 py-4">
      <div className="flex justify-between items-center">
        {/* Left side - Hello text */}
        <div>
          <h1 className="text-2xl font-medium">Hello {driverName},</h1>
        </div>

        {/* Right side - Status and Menu */}
        <div className="flex items-center">
          {/* Status Dropdown */}
          {canEditStatus ? (
            <div className="relative">
              <button
                ref={statusButtonRef}
                className="flex items-center hover:bg-background/10 px-2 py-1 rounded transition-colors"
                onClick={toggleStatusDropdown}
              >
                <div
                  className={`w-2 h-2 ${getStatusColor(currentStatus)} rounded-full mr-2`}
                ></div>
                <span className="text-sm font-medium mr-1">
                  {getStatusText(currentStatus)}
                </span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Status Dropdown Menu */}
              {showStatusDropdown && (
                <div
                  data-status-dropdown
                  className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[140px]"
                >
                  {statusOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleStatusSelect(option)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl flex items-center ${
                        currentStatus === option ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div
                        className={`w-2 h-2 ${getStatusColor(option)} rounded-full mr-2`}
                      ></div>
                      <span
                        className={`text-sm ${
                          currentStatus === option
                            ? 'text-primary font-medium'
                            : 'text-gray-700'
                        }`}
                      >
                        {getStatusText(option)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center opacity-50 px-2 py-1">
              <div
                className={`w-2 h-2 ${getStatusColor(currentStatus)} rounded-full mr-2`}
              ></div>
              <span className="text-sm font-medium">
                {getStatusText(currentStatus)}
              </span>
            </div>
          )}

          {/* Menu Button */}
          <button
            aria-label="Menu"
            className="hover:bg-background/10 p-1 rounded transition-colors"
            onClick={onMenuClick}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
